import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@carbon/react';
import { useAtom } from 'jotai';
import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import {
  FilterSection,
  ColumnDateFilter,
  ColumnMultiSelectFilter,
  useDataTableContext,
} from 'components/data-table';
import SearchableMultiSelect from 'components/searchable-multiselect/searchable-multiselect';
import type {
  CrmTractFacets,
  CRMAccountSearchResponse,
  EntityBaseResponse,
  EntityResponse,
} from 'types/api-responses';
import { formatMutationResource } from 'utils/misc';
import { crmAccountsSearchAtom } from './atoms';
import styles from './crm-tracts-filters.module.scss';

// ─────────────────────────────────────
// Option Item Type
// ─────────────────────────────────────

interface OptionItem {
  id: string;
  label: string;
  [key: string]: unknown;
}

// ─────────────────────────────────────
// Entity Searchable Filter
// ─────────────────────────────────────

// A remounted filter rebuilds its chips from the column's filter value, which holds ids only.
const pickedLabels = new Map<string, string>();

const selectedFromColumn = (
  value: unknown,
  facetOptions: OptionItem[]
): OptionItem[] =>
  ((value as string[] | undefined) ?? []).map((id) => ({
    id,
    label:
      pickedLabels.get(id) ??
      facetOptions.find((option) => option.id === id)?.label ??
      id,
  }));

/**
 * Searchable entity filter that shows initial facet options and triggers
 * backend search on typing. Syncs selection to DataTable column filter state.
 * @param {Object} props - The component props.
 * @return {JSX.Element} The entity searchable filter.
 */
const EntitySearchableFilter: FC<{
  facetOptions: OptionItem[];
  columnId: string;
}> = ({ facetOptions, columnId }) => {
  const { table } = useDataTableContext();
  const column = table.getColumn(columnId);

  // Atom created per-mount — search state intentionally resets when the
  // filter panel unmounts/remounts, which is acceptable for a search filter.
  const entitiesSearchAtoms = useMemo(
    () =>
      searchAtomPair<EntityResponse, SearchParams>('entities', {
        page: 1,
        pageSize: 25,
        text: '',
      }),
    []
  );
  const [entitiesSearchData, setEntitiesSearchData] = useAtom(
    entitiesSearchAtoms.searchAtom
  );

  const [options, setOptions] = useState<OptionItem[]>(facetOptions);
  const [selectedItems, setSelectedItems] = useState<OptionItem[]>(() =>
    selectedFromColumn(column?.getFilterValue(), facetOptions)
  );

  // Sync facet options on initial load
  useEffect(() => {
    if (facetOptions.length > 0 && options.length === 0) {
      setOptions(facetOptions);
    }
  }, [facetOptions, options.length]);

  // Update options when search results arrive — merge with selected items
  useEffect(() => {
    const results = entitiesSearchData.data?.results;
    if (!results) return;

    const searchOptions: OptionItem[] = results.map(
      (e: EntityBaseResponse) => ({
        id: String(e.id),
        label: e.name ?? String(e.id),
      })
    );

    const selectedNotInSearch = selectedItems.filter(
      (sel) => !searchOptions.some((opt) => opt.id === sel.id)
    );
    setOptions([...selectedNotInSearch, ...searchOptions]);
  }, [entitiesSearchData.data?.results, selectedItems]);

  const handleSearch = useCallback(
    (text: string) => {
      setEntitiesSearchData(() => ({ page: 1, pageSize: 25, text }));
    },
    [setEntitiesSearchData]
  );

  const handleSelectionChange = useCallback(
    (items: OptionItem[]) => {
      items.forEach((item) => pickedLabels.set(item.id, item.label));
      setSelectedItems(items);
      column?.setFilterValue(
        items.length > 0 ? items.map((i) => i.id) : undefined
      );
    },
    [column]
  );

  return (
    <SearchableMultiSelect<OptionItem>
      id="entity-filter"
      title="Insights Entity"
      placeholder="Select..."
      items={options}
      selectedItems={selectedItems}
      onSelectionChange={handleSelectionChange}
      onSearch={handleSearch}
      itemToString={(item) => item?.label ?? ''}
      className={styles.searchableFilter}
    />
  );
};

// ─────────────────────────────────────
// Account Searchable Filter
// ─────────────────────────────────────

/**
 * Searchable account filter that shows initial facet options and triggers
 * entity-scoped backend search on typing.
 * @param {Object} props - The component props.
 * @return {JSX.Element} The account searchable filter.
 */
const AccountSearchableFilter: FC<{
  facetOptions: OptionItem[];
  columnId: string;
  entityColumnId: string;
}> = ({ facetOptions, columnId, entityColumnId }) => {
  const { table } = useDataTableContext();
  const column = table.getColumn(columnId);
  const entityColumn = table.getColumn(entityColumnId);
  const selectedEntityIds = useMemo(
    () => (entityColumn?.getFilterValue() as string[]) ?? [],
    [entityColumn]
  );

  const [accountsSearchData, setAccountsSearchData] = useAtom(
    crmAccountsSearchAtom
  );
  const { data: accountsData } = formatMutationResource(accountsSearchData);

  const [options, setOptions] = useState<OptionItem[]>(facetOptions);
  const [selectedItems, setSelectedItems] = useState<OptionItem[]>(() =>
    selectedFromColumn(column?.getFilterValue(), facetOptions)
  );

  // Sync facet options on initial load
  useEffect(() => {
    if (facetOptions.length > 0 && options.length === 0) {
      setOptions(facetOptions);
    }
  }, [facetOptions, options.length]);

  // Update options when search results arrive — merge with selected items
  useEffect(() => {
    if (!accountsData?.results) return;

    const searchOptions: OptionItem[] = accountsData.results.map(
      // The tract filter matches account.salesforce_id, as the facet options do.
      (a: CRMAccountSearchResponse) => ({ id: a.salesforceId, label: a.name })
    );

    const selectedNotInSearch = selectedItems.filter(
      (sel) => !searchOptions.some((opt) => opt.id === sel.id)
    );
    setOptions([...selectedNotInSearch, ...searchOptions]);
  }, [accountsData?.results, selectedItems]);

  const handleSearch = useCallback(
    (text: string) => {
      setAccountsSearchData({
        url: '/queries/crm/accounts',
        type: 'POST',
        data: {
          query: {
            page: 1,
            pageSize: 25,
            text,
            ...(selectedEntityIds.length > 0
              ? { entityIds: selectedEntityIds }
              : {}),
          },
        },
      });
    },
    [setAccountsSearchData, selectedEntityIds]
  );

  const handleSelectionChange = useCallback(
    (items: OptionItem[]) => {
      items.forEach((item) => pickedLabels.set(item.id, item.label));
      setSelectedItems(items);
      column?.setFilterValue(
        items.length > 0 ? items.map((i) => i.id) : undefined
      );
    },
    [column]
  );

  return (
    <SearchableMultiSelect<OptionItem>
      id="account-filter"
      title="CRM Account"
      placeholder="Select..."
      items={options}
      selectedItems={selectedItems}
      onSelectionChange={handleSelectionChange}
      onSearch={handleSearch}
      itemToString={(item) => item?.label ?? ''}
      className={styles.searchableFilter}
    />
  );
};

// ─────────────────────────────────────
// Main Filter Panel
// ─────────────────────────────────────

interface CrmTractFiltersProps {
  facets: CrmTractFacets | undefined;
  /**
   * Whether the table is narrowed to tracts holding a vesting document. Passed in rather than
   * read from a column: `vestingStatus` is a search param the summary cards also write, not a
   * DataTable column filter, so the panel and the cards must read the one param or they drift.
   */
  hasVestingDocument: boolean;
  onHasVestingDocumentChange: (checked: boolean) => void;
  /** Same param the card writes. */
  hasExternalTitleSupport: boolean;
  onHasExternalTitleSupportChange: (checked: boolean) => void;
  portfolioAssets: boolean;
  onPortfolioAssetsChange: (checked: boolean) => void;
}

/**
 * Filter panel for the CRM Tract Library page. Date and instrument type use
 * standard DataTable column filters. Entity and account use SearchableMultiSelect
 * with backend search (entity-scoped for accounts).
 * @param {CrmTractFiltersProps} props - The component props.
 * @return {JSX.Element} The filter panel content.
 */
const CrmTractFilters: FC<CrmTractFiltersProps> = ({
  facets,
  hasVestingDocument,
  onHasVestingDocumentChange,
  hasExternalTitleSupport,
  onHasExternalTitleSupportChange,
  portfolioAssets,
  onPortfolioAssetsChange,
}) => {
  const toOptions = (items: { id: string; value: string }[] | undefined) =>
    (items ?? []).map((item) => ({ value: item.id, label: item.value }));

  const toSearchableOptions = (
    items: { id: string; value: string }[] | undefined
  ): OptionItem[] =>
    (items ?? []).map((item) => ({ id: item.id, label: item.value }));

  return (
    <>
      <FilterSection title="Date Filters">
        <ColumnDateFilter columnId="createdAt" label="Created Date" />
        <ColumnDateFilter columnId="updatedAt" label="Updated Date" />
      </FilterSection>

      <FilterSection title="CRM Filters">
        <EntitySearchableFilter
          facetOptions={toSearchableOptions(facets?.entities)}
          columnId="entityName"
        />

        <AccountSearchableFilter
          facetOptions={toSearchableOptions(facets?.accounts)}
          columnId="accountName"
          entityColumnId="entityName"
        />

        <ColumnMultiSelectFilter
          columnId="instrumentType"
          label="Instrument Type"
          options={toOptions(facets?.instrumentTypes)}
        />

        {/*
          Checked whenever the filter excludes `none` — so it stays checked when a summary card
          narrows further to just the verified rows, because those tracts do have a document.
          Unchecking clears the vesting filter outright rather than inverting it: there is no
          "no vesting document" view, for the reason VESTING_TILES documents.
        */}
        <Checkbox
          id="crm-tracts-has-vesting-document"
          labelText="Has Vesting Document"
          checked={hasVestingDocument}
          onChange={(_evt, { checked }: { checked: boolean }) =>
            onHasVestingDocumentChange(checked)
          }
        />

        <Checkbox
          id="crm-tracts-has-external-title-support"
          labelText="Has External Title Support"
          checked={hasExternalTitleSupport}
          onChange={(_evt, { checked }: { checked: boolean }) =>
            onHasExternalTitleSupportChange(checked)
          }
        />

        <Checkbox
          id="crm-tracts-portfolio-assets"
          labelText="Portfolio Assets"
          checked={portfolioAssets}
          onChange={(_evt, { checked }: { checked: boolean }) =>
            onPortfolioAssetsChange(checked)
          }
        />
      </FilterSection>
    </>
  );
};

export { CrmTractFilters };
